
import Vue from 'vue';
import _debounce from 'lodash/debounce';
import _flatten from 'lodash/flatten';
import _uniq from 'lodash/uniq';
import pluralize from 'pluralize';
import { mapActions, mapState } from 'vuex';
import { Company, Team, User } from '@run-diffusion/shared';
import { db, FieldValue, functions } from '@/firebase';
import { ROUTER } from '@/router/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';
import RolloutRow, { RolloutRowData } from './RolloutRow.vue';
import AlphaCsvReview from './AlphaCsvReview.vue';
import { membershipLabel } from './alphaCsv';

type RolloutKind = 'company' | 'team';
type Snapshot = { id: string, data: () => unknown };

const SEARCH_LIMIT: number = 8;
// A document id has no spaces or slashes; anything else is only searched as a name
const LOOKS_LIKE_ID: RegExp = /^[^\s/]{4,}$/;

const asCompany = (snap: Snapshot): Company => ({ ...(snap.data() as Company), id: snap.id });
const asTeam = (snap: Snapshot): Team => ({ ...(snap.data() as Team), id: snap.id });

// A person in the alpha, as the page lists them
type AlphaPerson = Pick<User, 'email' | 'teamIds'> & { id: string };
const asPerson = (snap: Snapshot): AlphaPerson => {
	const user = snap.data() as User;
	return { id: snap.id, email: user.email || '', teamIds: user.teamIds || {} };
};

/**
 * Admin > Boards v2 Rollout.
 *
 * Who has Boards v2: `features.boardsV2` on a team lets that team in ahead of
 * its company, and on a company lets every team in it in (the rule is
 * boardsV2Access in web/src/utils/boardsVersion.ts). Only RunDiffusion admins
 * may write either (firestore.rules). The two lists are live, so a switch
 * flipped here, or by another admin, shows at once.
 *
 * New accounts (IL-095): `masterConfigs/boardsV2.newAccounts` sends every new
 * account through the Boards v2 tour after its welcome survey, and starts it
 * in Boards v2 (web/src/utils/boardsTour.ts). Admins alone write it.
 *
 * The alpha (IL-107): `features.boardsV2` on a user lets that person in on
 * every account they use. Added from a CSV of emails (AlphaCsvReview.vue) and
 * taken out here, both through admin-only callables
 * (firebase/functions/src/useCases/boardsV2Alpha.ts), since a user doc's
 * rules let no one else write it. The list is live.
 */
export default Vue.extend({
	name: 'AdminBoardsV2Rollout',
	data () {
		return {
			companies: [] as Company[],
			teams: [] as Team[],
			loadingCompanies: true,
			loadingTeams: true,
			companyNames: {} as Record<string, string>,
			people: [] as AlphaPerson[],
			loadingPeople: true,
			peopleQuery: '' as string | null,
			// a person's teams, for their row: name and company
			teamInfo: {} as Record<string, { name: string, companyId: string | null }>,
			query: '' as string | null,
			searchTerm: '',
			searching: false,
			searchSeq: 0,
			foundCompanies: [] as Company[],
			foundTeams: [] as Team[],
			saving: {} as Record<string, boolean>,
			// remounts the switches, so one whose change was cancelled or failed shows the saved state
			resets: 0,
			confirm: { open: false, id: '', name: '', on: false },
			newAccounts: false,
			loadingNewAccounts: true,
			savingNewAccounts: false,
			confirmNew: { open: false, on: false },
			unsubscribes: [] as Array<() => void>,
			debouncedSearch: (() => {}) as (term: string) => void,
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		companiesOn (): Record<string, boolean> {
			return this.companies.reduce((acc: Record<string, boolean>, c: Company) => ({ ...acc, [c.id]: true }), {});
		},
		teamsOn (): Record<string, boolean> {
			return this.teams.reduce((acc: Record<string, boolean>, t: Team) => ({ ...acc, [t.id]: true }), {});
		},
		companyRows (): RolloutRowData[] {
			return this.companies.map(this.companyRow);
		},
		teamRows (): RolloutRowData[] {
			return this.teams.map(this.teamRow);
		},
		personRows (): RolloutRowData[] {
			const q: string = (this.peopleQuery || '').trim().toLowerCase();
			const rows: RolloutRowData[] = this.people.map(this.personRow);
			if (!q) return rows;
			return rows.filter((row: RolloutRowData) => row.name.toLowerCase().includes(q) || row.detail.toLowerCase().includes(q));
		},
		results (): RolloutRowData[] {
			return [
				...this.foundCompanies.map(this.companyRow),
				...this.foundTeams.map(this.teamRow),
			];
		},
	},
	watch: {
		query (q: string | null) {
			const term: string = (q || '').trim();
			this.searchTerm = term.length >= 2 ? term : '';
			this.searchSeq++;
			this.foundCompanies = [];
			this.foundTeams = [];
			this.searching = !!this.searchTerm;
			if (this.searchTerm) this.debouncedSearch(this.searchTerm);
		},
	},
	created () {
		this.debouncedSearch = _debounce(this.search, 300);
	},
	mounted () {
		this.unsubscribes.push(
			db.collection('companies').where('features.boardsV2', '==', true).onSnapshot((snap) => {
				this.companies = snap.docs.map(asCompany).sort(this.byName);
				this.rememberCompanyNames(this.companies);
				this.loadingCompanies = false;
			}, (e: Error) => {
				console.error('[boards v2 rollout] companies failed to load', e);
				this.loadingCompanies = false;
			}),
			db.doc('masterConfigs/boardsV2').onSnapshot((snap) => {
				const data = snap.exists ? snap.data() : null;
				this.newAccounts = !!(data && data.newAccounts === true);
				this.loadingNewAccounts = false;
			}, (e: Error) => {
				console.error('[boards v2 rollout] the new accounts switch failed to load', e);
				this.loadingNewAccounts = false;
			}),
			db.collection('users').where('features.boardsV2', '==', true).onSnapshot((snap) => {
				this.people = snap.docs.map(asPerson).sort((a: AlphaPerson, b: AlphaPerson) => a.email.localeCompare(b.email));
				this.loadTeamInfo(this.people);
				this.loadingPeople = false;
			}, (e: Error) => {
				console.error('[boards v2 rollout] the alpha failed to load', e);
				this.loadingPeople = false;
			}),
			db.collection('teams').where('features.boardsV2', '==', true).onSnapshot((snap) => {
				this.teams = snap.docs.map(asTeam).sort(this.byName);
				this.loadCompanyNames(this.teams);
				this.loadingTeams = false;
			}, (e: Error) => {
				console.error('[boards v2 rollout] teams failed to load', e);
				this.loadingTeams = false;
			}),
		);
	},
	beforeDestroy () {
		this.unsubscribes.forEach((unsubscribe: () => void) => unsubscribe());
		this.unsubscribes = [];
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		goBack () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
		},
		byName (a: { name?: string }, b: { name?: string }): number {
			return (a.name || '').localeCompare(b.name || '');
		},
		companyRow (company: Company): RolloutRowData {
			return {
				kind: 'company',
				id: company.id,
				name: company.name,
				detail: '',
				on: !!this.companiesOn[company.id],
				viaCompany: false,
			};
		},
		teamRow (team: Team): RolloutRowData {
			const members: number = Object.keys(team.userIds || {}).length;
			const companyName: string = this.companyNames[team.companyId] || '';
			return {
				kind: 'team',
				id: team.id,
				name: team.name,
				detail: [companyName, `${members} ${pluralize('member', members)}`].filter(Boolean).join(' · '),
				on: !!this.teamsOn[team.id],
				viaCompany: !!this.companiesOn[team.companyId],
			};
		},
		personRow (person: AlphaPerson): RolloutRowData {
			const teams = Object.keys(person.teamIds || {})
				.filter((id: string) => id in this.teamInfo)
				.map((id: string) => {
					const team = this.teamInfo[id];
					return { name: team.name, companyId: team.companyId, companyName: team.companyId ? this.companyNames[team.companyId] || '' : null };
				});
			return {
				kind: 'person',
				id: person.id,
				name: person.email,
				detail: membershipLabel(teams) || 'Personal account, on no team',
				on: true,
				viaCompany: false,
			};
		},
		// The teams people in the alpha are on, and those teams' companies
		async loadTeamInfo (people: AlphaPerson[]) {
			const missing: string[] = _uniq(_flatten(people.map((p: AlphaPerson) => Object.keys(p.teamIds || {})))).filter((id: string) => !(id in this.teamInfo));
			const loaded: Team[] = [];
			await Promise.all(missing.map(async (id: string) => {
				try {
					const snap = await db.collection('teams').doc(id).get();
					if (!snap.exists) return;
					const team: Team = asTeam(snap as Snapshot);
					this.$set(this.teamInfo, id, { name: team.name || '', companyId: team.companyId || null });
					loaded.push(team);
				} catch (e) {
					console.error('[boards v2 rollout] a team failed to load', id, e);
				}
			}));
			await this.loadCompanyNames(loaded);
		},
		rememberCompanyNames (companies: Company[]) {
			companies.forEach((c: Company) => this.$set(this.companyNames, c.id, c.name || ''));
		},
		async loadCompanyNames (teams: Team[]) {
			const missing: string[] = _uniq(teams.map((t: Team) => t.companyId).filter((id: string) => id && !(id in this.companyNames)));
			await Promise.all(missing.map(async (id: string) => {
				try {
					const snap = await db.collection('companies').doc(id).get();
					this.$set(this.companyNames, id, snap.exists ? (snap.data() as Company).name || '' : '');
				} catch (e) {
					console.error('[boards v2 rollout] company name failed to load', id, e);
				}
			}));
		},
		// Names from their start (as typed, and capitalized), and an exact document id
		async search (term: string) {
			const seq: number = this.searchSeq;
			const prefixes: string[] = _uniq([term, term.charAt(0).toUpperCase() + term.slice(1)]);
			const find = async (collection: 'companies' | 'teams'): Promise<Snapshot[]> => {
				const byName = prefixes.map((prefix: string) => db.collection(collection)
					.orderBy('name')
					.startAt(prefix)
					.endAt(prefix + '')
					.limit(SEARCH_LIMIT)
					.get()
					.then((snap) => snap.docs as Snapshot[]));
				const byId = LOOKS_LIKE_ID.test(term)
					? [db.collection(collection).doc(term).get().then((snap) => (snap.exists ? [snap as Snapshot] : []))]
					: [];
				const lists: Snapshot[][] = await Promise.all([...byId, ...byName]);
				const seen: Record<string, boolean> = {};
				return _flatten(lists).filter((snap: Snapshot) => {
					if (seen[snap.id]) return false;
					seen[snap.id] = true;
					return true;
				});
			};
			try {
				const [companies, teams] = await Promise.all([find('companies'), find('teams')]);
				if (seq !== this.searchSeq) return;
				this.foundCompanies = companies.map(asCompany);
				this.foundTeams = teams.map(asTeam);
				this.rememberCompanyNames(this.foundCompanies);
				await this.loadCompanyNames(this.foundTeams);
			} catch (e) {
				console.error('[boards v2 rollout] search failed', e);
				if (seq === this.searchSeq) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'Search failed, try again',
						show: true,
					});
				}
			} finally {
				if (seq === this.searchSeq) this.searching = false;
			}
		},
		onNewAccountsToggle (on: boolean) {
			this.confirmNew = { open: true, on };
		},
		onConfirmNewInput (open: boolean) {
			this.confirmNew = { ...this.confirmNew, open };
			// closed without turning it: put the switch back
			if (!open) this.resets++;
		},
		async onConfirmNew (close: () => void) {
			const on: boolean = this.confirmNew.on;
			this.savingNewAccounts = true;
			try {
				await db.doc('masterConfigs/boardsV2').set({ newAccounts: on }, { merge: true });
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: on ? 'New accounts start in Boards v2' : 'New accounts no longer start in Boards v2',
					show: true,
				});
			} catch (e) {
				console.error('[boards v2 rollout] failed to save the new accounts switch', e);
				this.resets++;
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Couldn\'t change the new accounts switch',
					show: true,
				});
			} finally {
				this.savingNewAccounts = false;
			}
			close();
		},
		onToggle (row: RolloutRowData, on: boolean) {
			if (row.kind === 'person') {
				this.writePerson(row.id, row.name, on);
				return;
			}
			if (row.kind === 'company') {
				this.confirm = { open: true, id: row.id, name: row.name || row.id, on };
				return;
			}
			this.write('team', row.id, row.name || row.id, on);
		},
		onConfirmInput (open: boolean) {
			this.confirm = { ...this.confirm, open };
			// closed without turning anything: put its switch back
			if (!open) this.resets++;
		},
		async onConfirmCompany (close: () => void) {
			await this.write('company', this.confirm.id, this.confirm.name, this.confirm.on);
			close();
		},
		// Takes a person out of the alpha (or back in), through the admin-only callable
		async writePerson (id: string, email: string, on: boolean) {
			const key: string = 'person:' + id;
			this.$set(this.saving, key, true);
			try {
				const result = await functions.httpsCallable('setBoardsV2Alpha')({ userIds: [id], on });
				const data = result.data as { success: boolean, message?: string };
				if (!data.success) throw new Error(data.message);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: on ? `${email} is in the Boards v2 alpha` : `${email} is out of the Boards v2 alpha`,
					show: true,
				});
			} catch (e) {
				console.error('[boards v2 rollout] failed to change the alpha', id, e);
				this.resets++;
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: (e as Error).message || `Couldn't change the alpha for ${email}`,
					show: true,
				});
			} finally {
				this.$set(this.saving, key, false);
			}
		},
		async write (kind: RolloutKind, id: string, name: string, on: boolean) {
			const key: string = kind + ':' + id;
			this.$set(this.saving, key, true);
			try {
				await db.collection(kind === 'company' ? 'companies' : 'teams').doc(id).update({
					'features.boardsV2': on ? true : FieldValue.delete(),
				});
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: on ? `Boards v2 is on for ${name}` : `Boards v2 is off for ${name}`,
					show: true,
				});
			} catch (e) {
				console.error('[boards v2 rollout] failed to save', kind, id, e);
				this.resets++;
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Couldn't change Boards v2 for ${name}`,
					show: true,
				});
			} finally {
				this.$set(this.saving, key, false);
			}
		},
	},
	components: {
		AlphaCsvReview,
		BaseToggle,
		ConfirmDialog,
		RolloutRow,
	},
});
